import generateUuid from 'uuid/v4';

import { isObject } from '@philips-emr/tws-utils';

import checkDuplicatedUUIDS from '../util/check-duplicated-uuids';

import { Node } from '.';

export const DUMMY_ROOT = 'DUMMY_ROOT';

function createDummyNode(): Node {
  return { type: 'Container', props: { uuid: DUMMY_ROOT }, children: [] };
}

/**
 * Handler to Fragment's metadata.
 *
 * Responsible to manage operations on metadata.
 */
class FragmentMetadata {

  static DUMMY_ROOT = DUMMY_ROOT;

  private name: string;
  private root: Node = createDummyNode();

  private componentMap = new Map<string, Node>();
  private parentBy = new Map<string, string>(); // childUUID, parentUUID

  constructor(name: string, content?: Node) {
    this.reset();

    this.name = name;
    content && this.load(content);
  }

  getName(): string {
    return this.name;
  }

  reset(): void {
    this.root = createDummyNode();

    this.componentMap = new Map();
    this.componentMap.set(DUMMY_ROOT, this.root);

    this.parentBy = new Map();
  }

  load(content: Node): void {
    this.addChildNode(DUMMY_ROOT, content, false);
    const root = this.componentMap.get(DUMMY_ROOT);

    if (root && root.children) {
      root.children.push(content);
    }
  }

  getRoot(): Node {
    return this.root;
  }

  getNode(uuid: string): Node | null {
    return this.componentMap.get(uuid) || null;
  }

  getTypedNode<T extends Node>(uuid: string): T | null {
    const node = this.componentMap.get(uuid);

    if (node) {
      return node as T;
    }

    return null;
  }

  getParent(uuid: string): string {
    return this.parentBy.get(uuid) || '';
  }

  createNode = (node: Node): Node => {
    const { type, props, children } = node;

    const uuid = generateUuid();
    const createdNode = {
      type,
      props: { uuid, ...props },
      children: children?.map(this.createNode),
    };

    return createdNode;
  };

  updateNode(uuid: string, props = {}): void {
    const component = this.componentMap.get(uuid);

    if (component) {
      Object.assign(component.props, props);
      this.cleanNode(component.props);
    }
  }

  private cleanNode(item: any): void {
    if (isObject(item)) {
      const itemRecord = item as Record<string, any>;
      this.removeNullItems(itemRecord);
    } 
  }

  private removeNullItems(item: Record<string, any>): void {
    Object.keys(item).forEach(key => {
      if (item[key] === null) {
        delete item[key];
      } else {
        this.cleanNode(item[key]);
      }
    });
  }

  addChildNode = (parent: string, node: Node, fillParent = true): void => {
    const isRoot = parent === DUMMY_ROOT;

    this.root = isRoot ? node : this.root;

    const { uuid } = node.props;

    this.componentMap.set(uuid, node);
    this.parentBy.set(uuid, parent);

    if (fillParent && this.componentMap.has(parent)) {
      const parentNode = this.componentMap.get(parent);

      if (parentNode) {
        if (!parentNode.children) {
          parentNode.children = [];
        }

        parentNode.children.push(node);
      }
    }

    if (node.children) {
      node.children.forEach(childNode => (
        this.addChildNode(uuid, childNode, false)
      ));
    }
  };

  deleteNode(uuid: string): void {
    const component = this.componentMap.get(uuid);

    if (!component) return;

    if (this.root === component) {
      this.reset();
    } else {
      const { children } = component;

      if (children) {
        children.forEach(child => this.deleteNode(child.props.uuid));
      }

      const parentUuid = this.parentBy.get(uuid) || '';
      const parent = this.getNode(parentUuid);

      if (parent && parent.children) {
        parent.children = parent.children.filter(child => child.props.uuid !== uuid);
      }

      this.componentMap.delete(uuid);
    }
  }

  validate(): boolean {
    const root = this.getRoot();

    try {
      checkDuplicatedUUIDS(root);
    } catch (e) {
      console.error(e); // eslint-disable-line no-console
      return false;
    }

    return true;
  }

}

export default FragmentMetadata;
